// Models tab on Admin › AI Prompts: the AI models a prompt version can use
// (ai_models). A model used by an Active or Draft version can't be made
// inactive until those versions move to another model.

// REACT IMPORTS
import React, { useEffect, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import {
	Alert, Button, CircularProgress, Dialog, DialogContent, DialogTitle, FormControlLabel, IconButton, Link, List, ListItem, ListItemText, MenuItem, Switch, Table, TableBody, TableCell,
	TableHead, TableRow, TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

// API, CONSTANTS
import { getAPICall, patchAPICall, postAPICall } from "../../config/apiCalls";
import { apiError } from "../ITBudget/itBudgetUtils";

const PROVIDERS = [
	{ value: "bedrock", label: "AWS Bedrock" },
	{ value: "anthropic", label: "Anthropic API" },
];
const providerLabel = (value) => PROVIDERS.find((p) => p.value === value)?.label || value;

const EMPTY = { name: "", provider: "bedrock", model_identifier: "", region: "", notes: "", sort_order: 0, active: true };

const ModelsTab = ({ canEdit }) => {
	const { instance, accounts } = useMsal();
	const [models, setModels] = useState(null);
	const [form, setForm] = useState(null); // the model being added or edited
	const [modulesOf, setModulesOf] = useState(null); // the model whose modules are listed
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		getAPICall(instance, accounts[0], "/api/ai-prompts/models")
			.then((res) => setModels(res.data))
			.catch((err) => setError(apiError(err, "Couldn't load models.")));
	}, []);

	const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

	const save = async () => {
		setBusy(true);
		setError("");
		try {
			const { id, in_use, modules, created, modified, modified_by, ...body } = form;
			const res = id
				? await patchAPICall(instance, accounts[0], `/api/ai-prompts/models/${id}`, body)
				: await postAPICall(instance, accounts[0], "/api/ai-prompts/models", body);
			setModels(res.data.models);
			setForm(null);
		} catch (err) {
			setError(apiError(err, "Couldn't save the model."));
		} finally {
			setBusy(false);
		}
	};

	if (!models) return <div className="itBudgetEmpty">{error ? <span className="itBudgetError">{error}</span> : <CircularProgress />}</div>;

	return (
		<div className="itBudgetCard">
			{error && !form && (
				<Alert severity="error" onClose={() => setError("")} style={{ marginBottom: 10 }}>
					{error}
				</Alert>
			)}
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Name</TableCell>
						<TableCell>Provider</TableCell>
						<TableCell>Identifier</TableCell>
						<TableCell>Region</TableCell>
						<TableCell>Notes</TableCell>
						<TableCell align="right">Modules Using It</TableCell>
						<TableCell>Status</TableCell>
						{canEdit && <TableCell />}
					</TableRow>
				</TableHead>
				<TableBody>
					{models.map((m) => (
						<TableRow key={m.id} style={m.active ? undefined : { opacity: 0.55 }}>
							<TableCell>{m.name}</TableCell>
							<TableCell>{providerLabel(m.provider)}</TableCell>
							<TableCell className="itBudgetMono">{m.model_identifier}</TableCell>
							<TableCell>{m.region || "—"}</TableCell>
							<TableCell>{m.notes || ""}</TableCell>
							<TableCell align="right">
								{m.modules.length ? (
									<Link component="button" variant="body2" onClick={() => setModulesOf(m)} aria-label={`Modules using ${m.name}`}>
										{m.modules.length}
									</Link>
								) : (
									0
								)}
							</TableCell>
							<TableCell>{m.active ? "Active" : "Inactive"}</TableCell>
							{canEdit && (
								<TableCell align="right">
									<IconButton size="small" aria-label={`Edit ${m.name}`} onClick={() => setForm({ ...m, region: m.region || "", notes: m.notes || "", active: Boolean(m.active) })}>
										<EditIcon fontSize="small" />
									</IconButton>
								</TableCell>
							)}
						</TableRow>
					))}
				</TableBody>
			</Table>
			{canEdit && (
				<Button startIcon={<AddIcon />} onClick={() => setForm({ ...EMPTY })} style={{ marginTop: 10 }}>
					Add Model
				</Button>
			)}

			{modulesOf && (
				<Dialog open onClose={() => setModulesOf(null)} fullWidth maxWidth="xs">
					<DialogTitle className="itBudgetDialogTitle">Modules using {modulesOf.name}</DialogTitle>
					<DialogContent className="itBudgetDialogContent">
						<div className="itBudgetMuted" style={{ marginBottom: 8 }}>
							Modules with an Active or Draft prompt version on this model
						</div>
						<List dense disablePadding>
							{modulesOf.modules.map((name) => (
								<ListItem key={name} disableGutters>
									<ListItemText primary={name} />
								</ListItem>
							))}
						</List>
						<div className="itBudgetDialogButtons">
							<Button onClick={() => setModulesOf(null)}>Close</Button>
						</div>
					</DialogContent>
				</Dialog>
			)}

			{form && (
				<Dialog open onClose={busy ? undefined : () => setForm(null)} fullWidth maxWidth="sm">
					<DialogTitle className="itBudgetDialogTitle">{form.id ? `Edit ${form.name}` : "Add Model"}</DialogTitle>
					<DialogContent className="itBudgetDialogContent" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
						{error && <Alert severity="error">{error}</Alert>}
						<TextField size="small" label="Name" required value={form.name} onChange={set("name")} helperText="Shown in the editor, e.g. Claude Haiku 5.5" />
						<TextField select size="small" label="Provider" required value={form.provider} onChange={set("provider")}>
							{PROVIDERS.map((p) => (
								<MenuItem key={p.value} value={p.value}>
									{p.label}
								</MenuItem>
							))}
						</TextField>
						<TextField
							size="small"
							label="Identifier"
							required
							value={form.model_identifier}
							onChange={set("model_identifier")}
							helperText={form.provider === "bedrock" ? "Bedrock model ID or inference profile, e.g. us.anthropic.claude-haiku-5-5" : "API model ID, e.g. claude-haiku-5-5"}
							inputProps={{ className: "itBudgetMono" }}
						/>
						<TextField size="small" label="Region" value={form.region} onChange={set("region")} helperText="AWS region the caller uses, e.g. us-west-2 (Bedrock only)" />
						<TextField size="small" label="Notes" multiline minRows={2} value={form.notes} onChange={set("notes")} />
						<TextField size="small" label="Sort Order" type="number" value={form.sort_order} onChange={set("sort_order")} />
						<FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />} label="Active — can be chosen for a version" />
						<div className="itBudgetDialogButtons">
							<Button onClick={() => setForm(null)} disabled={busy}>
								Cancel
							</Button>
							<Button variant="contained" onClick={save} disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : null}>
								Save
							</Button>
						</div>
					</DialogContent>
				</Dialog>
			)}
		</div>
	);
};

export default ModelsTab;
